import classNames from 'classnames';
import React, { memo, useEffect, useRef, useState } from 'react';
import { a11yKey } from '../../utils/ComponentUtil';
// eslint-disable-next-line
// @ts-ignore
import ReactToggleImport from 'react-toggle';
import 'react-toggle/style.css';
import styled from 'styled-components';
import { COLOR, fieldDisabledOpacity, fieldLabelFontSize, SIZE } from '../../styles';
import { FieldInfo } from '../FieldInfo';
import { type ToggleProps } from './Toggle.types';

/*
 * Without a moduler bundler the react-toggle export is an object with the default property which contains the component
 * This results in the following error by react-toggle
 * Warning: React.createElement: type is invalid -- expected a string (for built-in components) or a class/function (for composite components) but got: object.
 */

const ReactToggle = ReactToggleImport.default || ReactToggleImport;

let canChange = true;

const Toggle: React.FC<ToggleProps> = memo(
  ({
    focusOnLoad,
    checked,
    children,
    onChange,
    dataTestId = '',
    dataIntercomTarget,
    disabled,
    hintText,
    labelLeft,
    labelRight,
    name,
    title,
    value,
    theme,
  }) => {
    const labelRef = useRef<HTMLLabelElement | null>(null);
    const [hover, setHover] = useState(false);
    const [focus, setFocus] = useState(false);

    useEffect(() => {
      if (focusOnLoad && labelRef.current) {
        labelRef.current.focus();
        addFocus();
      }
    }, [focusOnLoad]);

    const addFocus = () => setFocus(true);
    const removeFocus = () => setFocus(false);
    const addHover = () => setHover(true);
    const removeHover = () => setHover(false);

    const setValue = (newValue: boolean) => {
      if (canChange) {
        onChange(newValue);
        // Throttle changes
        canChange = false;

        setTimeout(() => {
          canChange = true;
        }, 200);
      }
    };
    const onToggle = () => setValue(!checked);

    const onClick = (e: React.MouseEvent<HTMLLabelElement>) => {
      e.stopPropagation();
      removeFocus();
      onToggle();
    };

    const labelRightElement = labelRight ?? children;

    const double = labelLeft && labelRightElement;

    const fieldsetClass = classNames('toggle-field', {
      active: focus || hover,
      checked,
      disabled,
      left: labelLeft && !labelRightElement,
      right: !labelLeft && labelRightElement,
      double,
      dark: theme === 'dark',
    });

    const toggleKey = checked ? 37 : 39;
    const toggleKeyArray = [13, toggleKey];

    return (
      <Wrapper
        className={fieldsetClass}
        onFocus={addFocus}
        onBlur={removeFocus}
        onMouseOver={addHover}
        onMouseOut={removeHover}
        data-testid={
          dataTestId || dataIntercomTarget ? `${dataTestId || dataIntercomTarget}-toggle` : 'toggle'
        }
        data-intercom-target={dataIntercomTarget ? `${dataIntercomTarget}-toggle` : 'toggle'}
      >
        {title && <h3 className="toggle-title">{title}</h3>}
        {hintText && <FieldInfo text={hintText} />}
        <label
          data-testid={dataTestId ? `${dataTestId}-label` : 'label'}
          htmlFor={name}
          onClick={!double ? onClick : undefined}
          onKeyDown={a11yKey(onToggle, toggleKeyArray)}
          ref={labelRef}
          tabIndex={0}
        >
          {labelLeft && <span className="text left">{labelLeft}</span>}
          <ReactToggle
            checked={checked || false}
            disabled={disabled}
            readOnly={true}
            icons={false}
            name={name}
            tabIndex={-1}
            value={value}
          />
          {labelRightElement && <span className="text right">{labelRightElement}</span>}
        </label>
      </Wrapper>
    );
  },
);

// Styled Components
const toggleFieldHeight = SIZE['24'];

const Wrapper = styled.fieldset`
  border: none;
  font-family: inherit;
  margin: 0;
  padding: 0;
  vertical-align: middle;

  &.disabled {
    opacity: ${fieldDisabledOpacity};
    cursor: not-allowed;

    label {
      pointer-events: none;
    }
  }

  &.checked,
  &.double {
    label {
      color: ${COLOR['primary']};
    }
  }

  &.active:not(.disabled) label,
  label:focus,
  label:hover {
    color: ${COLOR['dark-gray-lighter']};
  }

  &.checked,
  &.double {
    &.active:not(.disabled) label,
    label:focus,
    label:hover {
      color: ${COLOR['primary-dark']};
      &.dark {
        color: ${COLOR['purple-light']};
      }
    }
  }

  &.dark {
    label {
      color: ${COLOR['light-gray']};
    }

    &.checked,
    &.double {
      label {
        color: ${COLOR['primary-light']};
      }
    }

    &.active.dark:not(.disabled) label,
    label:focus,
    label:hover {
      color: ${COLOR['light-gray-lighter']};
    }

    &.checked &.double {
      &.active:not(.disabled) label,
      label:focus,
      label:hover {
        color: ${COLOR['primary-light']};
      }
    }
  }

  &.active:not(.double, .disabled) {
    .react-toggle {
      .react-toggle-track {
        background: ${COLOR['dark-gray-lighter']};
      }

      .react-toggle-thumb {
        border-color: ${COLOR['dark-gray-lighter']};
      }
    }

    .react-toggle--checked {
      .react-toggle-track {
        background: ${COLOR['primary-dark']};
      }

      .react-toggle-thumb {
        border-color: ${COLOR['primary-dark']};
      }
    }

    &.dark {
      .react-toggle--checked {
        .react-toggle-track {
          background: ${COLOR['primary']};
        }

        .react-toggle-thumb {
          border-color: ${COLOR['primary']};
        }
      }
    }
  }

  &.double {
    &.active:not(.disabled) label,
    label:focus,
    label:hover {
      .react-toggle,
      .react-toggle--checked {
        .react-toggle-track {
          background: ${COLOR['primary-dark']};
        }

        .react-toggle-thumb {
          border-color: ${COLOR['primary-dark']};
        }
      }

      &.dark {
        .react-toggle,
        .react-toggle--checked {
          .react-toggle-track {
            background: ${COLOR['primary-light']};
          }

          .react-toggle-thumb {
            border-color: ${COLOR['primary-light']};
          }
        }
      }
    }

    .react-toggle,
    .react-toggle--checked {
      .react-toggle-track {
        background: ${COLOR['primary']};
      }

      .react-toggle-thumb {
        border-color: ${COLOR['primary']};
      }
    }
  }

  &.left {
    label {
      grid-template-columns: 1fr 50px;
    }
  }

  &.right {
    label {
      grid-template-columns: 50px 1fr;
    }
  }

  &.double {
    label {
      grid-template-columns: 1fr 50px 1fr;
    }
  }

  &,
  label {
    box-sizing: border-box;
    display: inline-block;
  }

  .toggle-title,
  label {
    color: ${COLOR['fieldLabelColor']};
    font-size: ${fieldLabelFontSize};
    padding: 0;
  }

  .toggle-title {
    font-weight: 400;
    margin: 0 0 ${SIZE['8']};

    & + .field-legend {
      margin-top: -3px;
      margin-bottom: 5px;
    }
  }

  label {
    align-content: center;
    cursor: pointer;
    display: grid;
    grid-gap: ${SIZE['8']};
    grid-template-rows: ${toggleFieldHeight};
    justify-content: center;
    align-items: center;
    line-height: ${SIZE['16']};
    margin: 0;
    transition: color 0.25s ease;
    width: 100%;

    &:focus {
      outline: none;
    }

    .react-toggle {
      .react-toggle-track {
        background: ${COLOR['dark-gray']};
      }

      .react-toggle-thumb {
        border-color: ${COLOR['dark-gray']};
        box-shadow: none !important;
      }
    }

    .react-toggle--checked {
      .react-toggle-track {
        background: ${COLOR['primary']};
      }

      .react-toggle-thumb {
        border-color: ${COLOR['primary']};
      }
    }

    > .text {
      font-weight: 500;
      line-height: calc(${toggleFieldHeight} + 1px);
      margin: 0;
    }
  }
`;

export default Toggle;
Toggle.displayName = 'Toggle';
